import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SectionLabel from '../components/SectionLabel';
import AnimatedSection from '../components/AnimatedSection';
import { getWebGames } from '../data/games';

const pulse = keyframes`
  0%,100% { opacity:1; transform:scale(1); }
  50%      { opacity:0.5; transform:scale(0.75); }
`;
const shimmer = keyframes`
  from { background-position: -200% center; }
  to   { background-position: 200% center; }
`;
const scanline = keyframes`
  0%   { transform:translateY(-100%); opacity:0; }
  10%  { opacity:0.4; }
  90%  { opacity:0.4; }
  100% { transform:translateY(400%); opacity:0; }
`;

const Section = styled.section`
  padding: 120px 80px;
  background: linear-gradient(to bottom, ${({theme})=>theme.colors.mid}, ${({theme})=>theme.colors.darker});
  position: relative;
  overflow: hidden;

  &::before {
    content: 'PLAY NOW';
    position: absolute;
    right: -60px; top: 50%;
    transform: translateY(-50%) rotate(90deg);
    font-family: ${({theme})=>theme.fonts.display};
    font-size: 120px;
    color: rgba(0,163,255,0.02);
    pointer-events: none;
    white-space: nowrap;
    letter-spacing: 0.05em;
  }

  @media (max-width:900px) { padding:80px 24px; }
`;

const Inner = styled.div`
  max-width: 1360px; margin: 0 auto;
`;

const Header = styled.div`
  display: flex; align-items: flex-end; justify-content: space-between;
  margin-bottom: 60px; flex-wrap: wrap; gap: 24px;
`;

const TitleWrap = styled.div``;

const Title = styled.h2`
  font-family: ${({theme})=>theme.fonts.display};
  font-size: clamp(52px,8vw,100px);
  line-height: 0.9; letter-spacing: 0.02em;
  .accent {
    background: ${({theme})=>theme.gradients.brand};
    background-size: 200% auto;
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    animation: ${shimmer} 3s linear infinite;
  }
`;

const LiveBadge = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  border: 1px solid rgba(0,255,136,0.3);
  padding: 8px 16px;
  font-family: ${({theme})=>theme.fonts.mono};
  font-size: 10px; letter-spacing: 0.3em;
  color: #00FF88; text-transform: uppercase;
  background: rgba(0,255,136,0.05);
  align-self: center;

  &::before {
    content: '';
    width: 7px; height: 7px;
    background: #00FF88; border-radius: 50%;
    animation: ${pulse} 1.5s ease infinite;
    box-shadow: 0 0 8px #00FF88;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width:900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width:600px) { grid-template-columns: 1fr; }
`;

const Card = styled(Link)`
  display: flex; flex-direction: column;
  background: ${({theme})=>theme.colors.surface};
  border: 1px solid ${({theme})=>theme.colors.border};
  text-decoration: none;
  overflow: hidden;
  position: relative;
  transition: all 0.4s;
  min-height: 360px;

  &::after {
    content: '';
    position: absolute; left:0; right:0;
    height:3px;
    background: linear-gradient(to right, transparent, ${({$accent})=>$accent}, transparent);
    opacity: 0; z-index: 3;
    animation: ${scanline} 2.5s linear infinite;
    animation-play-state: paused;
  }

  &:hover {
    border-color: ${({$accent})=>$accent}55;
    transform: translateY(-6px);
    box-shadow: 0 24px 60px rgba(0,0,0,0.5), 0 0 40px ${({$accent})=>$accent}15;
    &::after { animation-play-state: running; opacity: 1; }
  }
`;

const CardHeader = styled.div`
  position: relative;
  height: 200px;
  background: ${({$bg})=>$bg};
  display: flex; align-items: center; justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
`;

const CardEmoji = styled.div`
  font-size: 80px;
  filter: drop-shadow(0 0 20px ${({$accent})=>$accent}88);
  transition: transform 0.4s;
  z-index: 1;
  ${Card}:hover & { transform: scale(1.12) translateY(-6px); }
`;

const CardGlow = styled.div`
  position: absolute; inset: 0;
  background: radial-gradient(ellipse 70% 50% at 50% 80%, ${({$accent})=>$accent}18 0%, transparent 70%);
  opacity: 0; transition: opacity 0.4s;
  ${Card}:hover & { opacity: 1; }
`;

const AccentLine = styled.div`
  position: absolute; bottom:0; left:0;
  height: 2px; width: 0%;
  background: ${({$accent})=>$accent};
  box-shadow: 0 0 12px ${({$accent})=>$accent};
  transition: width 0.5s ease;
  ${Card}:hover & { width: 100%; }
`;

const CardBody = styled.div`
  padding: 28px 26px;
  @media(max-width:480px){padding:22px 18px}
  flex: 1; display: flex; flex-direction: column;
`;

const CardTop = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 14px;
`;

const StatusDot = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  font-family: ${({theme})=>theme.fonts.mono};
  font-size: 9px; letter-spacing: 0.25em;
  color: ${({$c})=>$c}; text-transform: uppercase;
  border: 1px solid ${({$c})=>$c}33; padding: 3px 10px;
  &::before {
    content:''; width:5px;height:5px;
    background:${({$c})=>$c};border-radius:50%;
    animation:${pulse} 2s infinite;
  }
`;

const Platform = styled.span`
  font-family: ${({theme})=>theme.fonts.mono};
  font-size: 8px; letter-spacing: 0.15em;
  color: rgba(255,255,255,0.78);
  border: 1px solid rgba(255,255,255,0.08);
  padding: 2px 8px; text-transform: uppercase;
`;

const CardTitle = styled.h3`
  font-family: ${({theme})=>theme.fonts.display};
  font-size: 38px; letter-spacing: 0.04em;
  color: #fff; margin-bottom: 8px;
  transition: all 0.3s;
  ${Card}:hover & {
    background: ${({$accent})=>`linear-gradient(135deg, #fff 40%, ${$accent})`};
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  }
`;

const CardGenre = styled.div`
  font-size: 11px; letter-spacing: 0.22em;
  color: rgba(255,255,255,0.82); text-transform: uppercase;
  font-weight: 600; margin-bottom: 12px;
`;

const CardDesc = styled.p`
  font-size: 14px; line-height: 1.72;
  color: ${({theme})=>theme.colors.textSecondary};
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2; overflow: hidden;
`;

const PlayBtn = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  margin-top: 20px;
  font-family: ${({theme})=>theme.fonts.mono};
  font-size: 11px; letter-spacing: 0.25em;
  color: ${({$accent})=>$accent}; text-transform: uppercase;
  opacity: 0; transform: translateY(6px);
  transition: all 0.3s;
  ${Card}:hover & { opacity:1; transform:translateY(0); }
  span { transition: transform 0.3s; }
  ${Card}:hover span { transform: translateX(5px); }
`;

export default function WebGamesSection() {
  const { t } = useTranslation();
  const webGames = getWebGames();

  return (
    <Section id="jogar">
      <Inner>
        <AnimatedSection>
          <Header>
            <TitleWrap>
              <SectionLabel>{t('webGames.sectionLabel')}</SectionLabel>
              <Title>{t('webGames.titlePrefix')} <span className="accent">{t('webGames.titleAccent')}</span></Title>
            </TitleWrap>
            <LiveBadge>{t('webGames.liveBadge')}</LiveBadge>
          </Header>
        </AnimatedSection>

        <Grid>
          {webGames.map((game, i) => (
            <AnimatedSection key={game.id} delay={i * 100}>
              <Card to={game.route} $accent={game.accentColor}>
                <CardHeader $bg={game.gradient}>
                  <CardEmoji $accent={game.accentColor}>{game.emoji}</CardEmoji>
                  <CardGlow $accent={game.accentColor} />
                  <AccentLine $accent={game.accentColor} />
                </CardHeader>

                <CardBody>
                  <CardTop>
                    <StatusDot $c={game.statusColor}>{game.status}</StatusDot>
                    <Platform>WEB</Platform>
                  </CardTop>
                  <CardTitle $accent={game.accentColor}>{game.title}</CardTitle>
                  <CardGenre>{game.genre}</CardGenre>
                  <CardDesc>{game.description}</CardDesc>
                  <PlayBtn $accent={game.accentColor}>
                    {t('webGames.playNow')} <span>→</span>
                  </PlayBtn>
                </CardBody>
              </Card>
            </AnimatedSection>
          ))}
        </Grid>
      </Inner>
    </Section>
  );
}
